import {
  type InfiniteData,
  type UseInfiniteQueryOptions,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { getNotifications, markAllNotificationsAsRead, markNotificationAsRead } from '@/services/notification.service';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { type NotificationsResponse } from '@/types/notifications';
import { ToastVariant } from '@/components/ui/toast';
import { getNextPageParam } from '@/lib/pagination';
import { toast } from '@/components/ui/use-toast';
import { QueryKey } from '@/lib/queryKeys';
import { useTranslations } from 'next-intl';

export const markNotificationMutationKey = [QueryKey.NotificationList, 'mark-notification'] as const;

export const useNotifications = (
  params: {
    pagination: { limit: number | null; offset: number | null };
    sort: { order: string; value: string };
  },
  options?: Omit<
    UseInfiniteQueryOptions<NotificationsResponse, Error, InfiniteData<NotificationsResponse>>,
    'queryKey' | 'queryFn' | 'initialPageParam' | 'getNextPageParam'
  >,
) => {
  const queryClient = useQueryClient();
  const notificationsQueryFn = useAuthenticatedQueryFn(getNotifications);
  const markAllAsReadQueryFn = useAuthenticatedQueryFn(markAllNotificationsAsRead);
  const markNotificationAsReadQueryFn = useAuthenticatedQueryFn(markNotificationAsRead);

  const { data, isLoading, isError, isFetching, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery<NotificationsResponse, Error, InfiniteData<NotificationsResponse>>({
      queryKey: [QueryKey.NotificationList, params?.pagination, params?.sort],
      initialPageParam: params?.pagination?.offset ?? 0,
      queryFn: ({ pageParam }) =>
        notificationsQueryFn({
          ...params,
          pagination: { ...params.pagination, offset: pageParam as number },
        }),
      getNextPageParam: (lastPage) => getNextPageParam(lastPage, params?.pagination?.limit ?? null),
      placeholderData: keepPreviousData,
      ...options,
    });

  const notifications = data?.pages?.flatMap((page) => page.notifications) ?? [];
  const unreadCount = data?.pages?.[0]?.unreadCount ?? 0;
  const lastPage = data?.pages?.[data.pages.length - 1];
  const pagination = lastPage?.pagination;

  const { mutateAsync: markAllAsRead, isPending: isMarkingAll } = useMutation({
    mutationFn: () => markAllAsReadQueryFn(undefined as never),
    onSuccess: () => {
      const updatedAt = new Date().toISOString();
      const notificationQueries = queryClient.getQueriesData({
        queryKey: [QueryKey.NotificationList],
      });

      notificationQueries.forEach(([key]) => {
        queryClient.setQueryData(key, (current: any) => {
          if (!current?.pages) {
            return current;
          }

          const updatedPages = current.pages.map((page: NotificationsResponse) => ({
            ...page,
            unreadCount: 0,
            notifications: page.notifications.map((notification) => ({
              ...notification,
              readAt: notification.readAt ?? updatedAt,
            })),
          }));

          return {
            ...current,
            pages: updatedPages,
          };
        });
      });
    },
    // TODO: Check if we really need this
    onSettled: () => queryClient.invalidateQueries({ queryKey: [QueryKey.NotificationList] }),
  });

  const tCommon = useTranslations('common');
  const tErrors = useTranslations('errors');

  const { mutateAsync: markNotificationRead, isPending: isMarkingSingle } = useMutation({
    mutationKey: markNotificationMutationKey,
    mutationFn: ({ notificationId, read }: { notificationId: string; read: boolean }) =>
      markNotificationAsReadQueryFn({ notificationId, read }),
    onSuccess: (updatedNotification, { notificationId, read }) => {
      const updatedAt = new Date().toISOString();
      const notificationQueries = queryClient.getQueriesData<InfiniteData<NotificationsResponse>>({
        queryKey: [QueryKey.NotificationList],
      });

      notificationQueries.forEach(([key]) => {
        queryClient.setQueryData<InfiniteData<NotificationsResponse>>(key, (current) => {
          if (!current?.pages) {
            return current;
          }

          let delta = 0;
          let targetPageIndex = -1;

          const updatedPages = current.pages.map((page, pageIndex) => {
            const updatedNotifications = page.notifications.map((notification) => {
              if (notification.id !== notificationId) {
                return notification;
              }

              const wasPreviouslyUnread = !notification.readAt;
              const wasPreviouslyRead = Boolean(notification.readAt);

              if (read && wasPreviouslyUnread) {
                delta = -1;
              } else if (!read && wasPreviouslyRead) {
                delta = 1;
              } else {
                delta = 0;
              }
              targetPageIndex = pageIndex;

              return {
                ...notification,
                readAt: read ? (updatedNotification?.readAt ?? notification.readAt ?? updatedAt) : null,
              };
            });

            return {
              ...page,
              notifications: updatedNotifications,
              unreadCount:
                pageIndex === targetPageIndex ? Math.max(0, (page.unreadCount ?? 0) + delta) : page.unreadCount,
            };
          });

          // Keep the badge in sync using the first page count as the source of truth.
          if (delta !== 0 && updatedPages[0]) {
            updatedPages[0] = {
              ...updatedPages[0],
              unreadCount:
                targetPageIndex === 0
                  ? updatedPages[0].unreadCount
                  : Math.max(0, (updatedPages[0].unreadCount ?? 0) + delta),
            };
          }

          return {
            ...current,
            pages: updatedPages,
          };
        });
      });
    },
    onError: (error: any, { notificationId }: { notificationId: string; read: boolean }) => {
      if (error.status === 404) {
        const updatedAt = new Date().toISOString();
        const queryCache = queryClient.getQueryCache();
        const queries = queryCache.findAll({ queryKey: [QueryKey.NotificationList] });

        queries.forEach((query) => {
          queryClient.setQueryData<InfiniteData<NotificationsResponse>>(query.queryKey, (current) => {
            if (!current?.pages) {
              return current;
            }

            const updatedPages = current.pages.map((page) => ({
              ...page,
              notifications: page.notifications.map((notification) => {
                if (notification.id !== notificationId) {
                  return notification;
                }
                return {
                  ...notification,
                  readAt: notification.readAt ?? updatedAt,
                };
              }),
            }));

            return {
              ...current,
              pages: updatedPages,
            };
          });
        });
        return;
      }

      toast({
        variant: ToastVariant.DESTRUCTIVE,
        title: tCommon('error'),
        description: error.message ?? tErrors('toastDefaultDescription'),
      });
    },
  });

  return {
    notifications,
    unreadCount,
    pagination,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    isFetching,
    refetch,
    markAllAsRead,
    isMarkingAll,
    markNotificationRead,
    isMarkingSingle,
  };
};
